import React from 'react';
import styled from 'styled-components';
import { graphql } from 'gatsby';

import { Card as SC_Card } from '@keytrade/components-card';

import { useLocalization } from '@plugins/keytrade-localization/useLocalization';
import ArticleTag, { ArticleTagType } from '@/components/blog/ArticleTag';
import { PageInfoType } from '@/components/Page';
import Title from '@/components/Title';
import Image, { ImageType } from '@/components/Image';
import { mediaQuery } from '@/utils/helpers';

export type ArticleBlockType = {
  contentful_id: string;
  title: string;
  tags: Array<ArticleTagType>;
  page: PageInfoType;
  image: ImageType;
  index: number;
  blogPath: string;
  margin: string;
  hasTags: boolean;
  date: string;
};
type ArticleContentMargin = {
  margin?: string;
};
const ArticleContent = styled.li<ArticleContentMargin>`
  list-style-type: none;
  min-width: 260px;
  margin: 1.2rem;
  scroll-snap-align: center;
  ${mediaQuery.md(`
    min-width: 360px;
    width: calc(100% / 3);
  `)}
`;
const Card = styled(SC_Card)`
  overflow: hidden;
  height: 100%;
  width: 100%;
`;
const ArticleImageContent = styled.div`
  position: relative;
  width: 100%;
  height: 160px;
  > .gatsby-image-wrapper,
  > img {
    height: 100%;
    width: 100%;
  }
  svg {
    width: 100%;
  }
  ${mediaQuery.md(`
  height: 240px;
`)}
`;
const ArticleInfoContent = styled.div`
  text-align: left;
  padding: 2.4rem 2.4rem 3.2rem;
`;

const TagsContent = styled.div`
  position: absolute;
  left: 2.8rem;
  bottom: 2.2rem;
`;

type Props = ArticleBlockType;
const ArticleBlock: React.FC<Props> = ({
  title,
  tags,
  page,
  image,
  blogPath,
  margin,
  hasTags,
}) => {
  const { locale, localizedPath } = useLocalization();
  const articleLink = localizedPath(locale, `${blogPath}/${page[0].slug}`);

  return (
    <ArticleContent margin={margin}>
      <Card href={articleLink} fillContainer>
        <ArticleImageContent>
          <Image {...image} />
          {hasTags && tags && (
            <TagsContent>
              {React.Children.toArray(
                tags.map((tag) => {
                  return ArticleTag({ ...tag });
                }),
              )}
            </TagsContent>
          )}
        </ArticleImageContent>
        <ArticleInfoContent>
          <Title size='sm' level='h2'>
            {title}
          </Title>
        </ArticleInfoContent>
      </Card>
    </ArticleContent>
  );
};

export default ArticleBlock;

export const articleQuery = graphql`
  fragment ArticleBlockFragment on ContentfulArticle {
    contentful_id
    title
    date
    image {
      ...ImageFragment
      gatsbyImageData(aspectRatio: 1.5)
    }

    tags {
      ...ArticleTagFragment
    }
    page {
      ...PagePathFragment
    }
  }
`;
